export interface ContentfulCategory {
  id: string
  title: string
  description: string
  slug: string
  topics: ContentfulTopic[]
}

export interface CategoryPageContext {
  contentfulCategory: ContentfulCategory
}

export interface ContentfulTopic {
  id: string
  title: string
  slug: string
  subtopics: ContentfulSubtopic[]
  category: ContentfulCategory[]
}

export interface TopicPageContext {
  contentfulTopic: ContentfulTopic
  contentfulCategory: ContentfulCategory
}

export interface ContentfulSubtopic {
  id: string
  title: string
  slug: string
  description: string
  articles: ContentfulHelpCenterArticle[]
  containsSequencedArticles: boolean
  topic: ContentfulTopic[]
}

export interface SubtopicPageContext {
  contentfulSubtopic: ContentfulSubtopic
  contentfulTopic: ContentfulTopic
  contentfulCategory: ContentfulCategory
}

export interface ContentfulHelpCenterArticle {
  id: string
  contentful_id: string
  title: string
  slug: string
  description: {
    childMarkdownRemark: {
      rawMarkdownBody: string
    }
  }
  article: {
    childMarkdownRemark: {
      html: string
    }
  }
  relatedArticles: ContentfulHelpCenterArticle[]
  subtopic: ContentfulSubtopic[]
  requiresAuthenticationToView: boolean
}

export interface ArticlesPageData {
  featuredArticles: {
    nodes: ContentfulHelpCenterArticle[]
  }
  featuredCategories: {
    nodes: ContentfulCategory[]
  }
}

export interface HelpCenterArticlePageContext {
  contentfulHelpCenterArticle: ContentfulHelpCenterArticle
}

export interface ContentfulSearchResult {
  "contentful-id": string
  title: string
  slug: string
  "content-type": ContentfulSearchResultContentType
}

export enum ContentfulSearchResultContentType {
  HelpCenterArticle = "helpCenterArticle",
  Category = "category",
}

export interface HomePageData {
  site: {
    siteMetadata: {
      title: string
      description: string
    }
  }
}

export interface ContentfulPodcastSeries {
  id: string
  title: string
  description: string
  slug: string
  podcastEpisodes: ContentfulPodcastEpisode[]
}

export interface ContentfulPodcastEpisode {
  id: string
  contentful_id: string
  title: string
  slug: string
  episodeLink?: string
  description: {
    childMarkdownRemark: {
      html: string
    }
  }
  seoDescription?: {
    childMarkdownRemark: {
      rawMarkdownBody: string
    }
  }
  thumbnail: {
    url: string
    title: string
  }
  podcast_series?: ContentfulPodcastSeries[]
}

export interface PodcastEpisodePageContext {
  contentfulPodcastEpisode: ContentfulPodcastEpisode
}

export interface ContentfulCommunityEvent {
  id: string
  contentful_id: string
  title: string
  slug: string
  date: string
  videoLink?: string
  eventImage?: {
    url: string
    title: string
  }
  description: {
    childMarkdownRemark: {
      html: string
    }
  }
  seoDescription?: {
    childMarkdownRemark: {
      rawMarkdownBody: string
    }
  }
  thumbnail: {
    url: string
    title: string
  }
  requiresAuthenticationToView: boolean
}

export interface CommunityEventPageContext {
  contentfulCommunityEvent: ContentfulCommunityEvent
}

export interface CommunityPageData {
  podcastSeries: {
    nodes: ContentfulPodcastSeries[]
  }
  communityEvents: {
    nodes: ContentfulCommunityEvent[]
  }
}

export interface ContentfulAcademyCourse {
  id: string
  contentful_id: string
  title: string
  slug: string
  description: {
    childMarkdownRemark: {
      rawMarkdownBody: string
    }
  }
  instructors: ContentfulAcademyInstructor[]
  lessons: ContentfulAcademyLesson[]
}

export enum ContentfulAcademyCourseStatus {
  InProgress = "in_progress",
  Completed = "completed",
}

export interface AcademyCourseProgress {
  id: string
  status: ContentfulAcademyCourseStatus
  last_updated_at: string
}

export interface ContentfulAcademyInstructor {
  id: string
  name: string
  title: string
  description: {
    childMarkdownRemark: {
      rawMarkdownBody: string
    }
  }
  photo: {
    url: string
    title: string
  }
}

export interface ContentfulAcademyLesson {
  id: string
  contentful_id: string
  title: string
  slug: string
  estimatedMinutesToComplete: string
  videoLink: string
  seoDescription: {
    childMarkdownRemark: {
      rawMarkdownBody: string
    }
  }
  content: {
    childMarkdownRemark: {
      html: string
    }
  }
  requiresAuthenticationToView: boolean
}

export enum ContentfulAcademyLessonStatus {
  InProgress = "in_progress",
  Completed = "completed",
}

export interface AcademyLessonProgress {
  id: string
  status: ContentfulAcademyLessonStatus
  last_updated_at: string
}

export interface AcademyPageData {
  allContentfulAcademyCourseList: {
    nodes: ContentfulAcademyCourse[]
  }
}

export interface AcademyLessonPageContext {
  contentfulAcademyCourse: ContentfulAcademyCourse
  contentfulAcademyLesson: ContentfulAcademyLesson
}

export interface ContentfulEnterpriseCustomer {
  name: string
  slug: string
  description: {
    childMarkdownRemark: {
      html: string
    }
  }
  enterpriseEndUserArticles: ContentfulEnterpriseEndUserArticle[]
}

export interface ContentfulEnterpriseEndUserArticle {
  contentful_id: string
  title: string
  slug: string

  locale: {
    code: string
    languageName: string
  }

  description: {
    childMarkdownRemark: {
      rawMarkdownBody: string
    }
  }
  coverImage: {
    url: string
    title: string
  }
  article: {
    childMarkdownRemark: {
      html: string
    }
  }
  relatedArticles: ContentfulEnterpriseEndUserArticle[]
  enterprise_customer: ContentfulEnterpriseCustomer[]
}

export interface EnterpriseEndUserArticlePageContext {
  contentfulEnterpriseEndUserArticle: ContentfulEnterpriseEndUserArticle
}

export type PageContext =
  | CategoryPageContext
  | TopicPageContext
  | SubtopicPageContext
  | HelpCenterArticlePageContext
  | PodcastEpisodePageContext
  | CommunityEventPageContext
  | AcademyLessonPageContext
  | EnterpriseEndUserArticlePageContext
